import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, AlertCircle, Play, RotateCcw, ListOrdered, CalendarClock, X } from 'lucide-react';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyBorrowsPage = () => {
  const [borrows, setBorrows] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { borrowCount, fetchBorrowCount } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [borrowsRes, waitlistRes, reservationsRes] = await Promise.all([
        axios.get(`${API}/borrows`, { withCredentials: true }),
        axios.get(`${API}/waitlist`, { withCredentials: true }),
        axios.get(`${API}/reservations`, { withCredentials: true })
      ]);
      setBorrows(borrowsRes.data);
      setWaitlist(waitlistRes.data);
      setReservations(reservationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm('Cancel this pre-booking?')) return;
    try {
      await axios.delete(`${API}/reservations/${reservationId}`, { withCredentials: true });
      toast.success('Pre-booking cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const handleReturn = async (borrowId) => {
    try {
      await axios.post(`${API}/borrows/${borrowId}/return`, {}, { withCredentials: true });
      toast.success('Movie returned successfully');
      fetchData();
      fetchBorrowCount();
    } catch (error) {
      toast.error('Failed to return movie');
    }
  };

  const handleReturnAll = async () => {
    const activeCount = borrows.filter(b => b.status === 'active').length;
    if (!window.confirm(`Are you sure you want to return all ${activeCount} movies?`)) {
      return;
    }
    try {
      const { data } = await axios.post(`${API}/borrows/return-all`, {}, { withCredentials: true });
      toast.success(`Returned ${data.count} movie${data.count !== 1 ? 's' : ''} successfully`);
      fetchData();
      fetchBorrowCount();
    } catch (error) {
      toast.error('Failed to return movies');
    }
  };

  const handleLeaveWaitlist = async (waitlistId) => {
    try {
      await axios.delete(`${API}/waitlist/${waitlistId}`, { withCredentials: true });
      toast.success('Left waitlist successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to leave waitlist');
    }
  };

  const calculateDaysLeft = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) return { days: 0, hours: 0, expired: true };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return { days, hours, expired: false };
  };

  const calculateWaitDays = (joinedAt) => {
    const now = new Date();
    const joined = new Date(joinedAt);
    const diff = now - joined;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  const activeBorrows = borrows.filter(b => b.status === 'active');
  const pastBorrows = borrows.filter(b => b.status !== 'active');

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-8 md:px-12 lg:px-16 pb-12" style={{ backgroundColor: '#0F171E' }} data-testid="my-borrows-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }}>My Borrows</h1>
            <p style={{ color: '#8197A4' }}>Track your borrowed movies, pre-bookings, and waitlist</p>
          </div>
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="borrow-stats-card">
            <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#8197A4' }}>Borrow Slots</p>
            <p className="text-2xl font-bold" style={{ color: '#3B82F6' }}>
              {borrowCount.active_count} <span style={{ color: '#8197A4' }}>/ {borrowCount.max_limit}</span>
            </p>
          </div>
        </div>

        {/* Active Borrows */}
        {activeBorrows.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Active Borrows</h2>
              {activeBorrows.length >= 1 && (
                <button
                  onClick={handleReturnAll}
                  className="flex items-center gap-2 px-5 py-3 rounded-lg font-bold transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: '#DC2626',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(220, 38, 38, 0.35)'
                  }}
                  data-testid="return-all-button"
                >
                  <RotateCcw size={20} />
                  Return All ({activeBorrows.length})
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4">
              {activeBorrows.map((borrow) => {
                const timeLeft = calculateDaysLeft(borrow.expires_at);
                const isExpiringSoon = timeLeft.days <= 1;
                
                return (
                  <div
                    key={borrow.borrow_id}
                    className="p-6 rounded-xl flex items-center gap-6 flex-wrap"
                    style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}
                    data-testid={`active-borrow-${borrow.borrow_id}`}
                  >
                    <img
                      src={mediaUrl(borrow.movie?.poster_path, API) || `https://picsum.photos/seed/${borrow.movie_id}/200/300`}
                      alt={borrow.movie?.title}
                      className="w-24 h-36 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-[200px]">
                      <h3 className="font-semibold text-xl mb-2" style={{ color: '#FFFFFF' }}>
                        {borrow.movie?.title || 'Unknown Movie'}
                      </h3>
                      
                      {/* Days remaining highlight */}
                      <div className="flex items-center gap-2 mb-3 p-3 rounded-lg inline-flex" style={{
                        backgroundColor: isExpiringSoon ? 'rgba(240, 193, 75, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                        border: `1px solid ${isExpiringSoon ? '#F0C14B' : '#3B82F6'}`
                      }}>
                        <Clock size={18} style={{ color: isExpiringSoon ? '#F0C14B' : '#3B82F6' }} />
                        <span className="text-sm font-semibold" style={{ color: '#FFFFFF' }} data-testid={`days-remaining-${borrow.borrow_id}`}>
                          {timeLeft.days} days {timeLeft.hours}h remaining
                        </span>
                      </div>

                      <p className="text-xs mb-4" style={{ color: '#8197A4' }}>
                        Borrowed on {new Date(borrow.borrowed_at).toLocaleDateString()} • Expires on {new Date(borrow.expires_at).toLocaleDateString()}
                      </p>

                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => navigate(`/watch/${borrow.movie_id}`)}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:scale-105"
                          style={{ backgroundColor: '#3B82F6', color: 'white' }}
                          data-testid={`watch-button-${borrow.borrow_id}`}
                        >
                          <Play size={16} />
                          Watch Now
                        </button>
                        <button
                          onClick={() => handleReturn(borrow.borrow_id)}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                          style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
                          data-testid={`return-button-${borrow.borrow_id}`}
                        >
                          <RotateCcw size={16} />
                          Return Now
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pre-bookings Section */}
        {reservations.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#FFFFFF' }}>
              <CalendarClock size={24} style={{ color: '#10B981' }} />
              My Pre-bookings <span className="text-sm font-normal" style={{ color: '#8197A4' }}>({reservations.length})</span>
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {reservations.map((r) => {
                const scheduled = r.scheduled_date ? new Date(r.scheduled_date) : null;
                const endDate = scheduled ? new Date(scheduled.getTime() + 5 * 24 * 60 * 60 * 1000) : null;
                return (
                  <div
                    key={r.reservation_id}
                    className="p-6 rounded-xl flex items-center gap-6 flex-wrap"
                    style={{ backgroundColor: '#1A2632', border: '1px solid #10B981' }}
                    data-testid={`reservation-item-${r.reservation_id}`}
                  >
                    <img
                      src={mediaUrl(r.movie?.poster_path, API) || `https://picsum.photos/seed/${r.movie_id}/200/300`}
                      alt={r.movie?.title}
                      className="w-24 h-36 object-cover rounded-lg cursor-pointer"
                      onClick={() => navigate(`/movie/${r.movie_id}`)}
                    />
                    <div className="flex-1 min-w-[200px]">
                      <h3 className="font-semibold text-xl mb-2" style={{ color: '#FFFFFF' }}>
                        {r.movie?.title || 'Unknown Movie'}
                      </h3>

                      <div className="flex items-center gap-2 mb-3 p-3 rounded-lg inline-flex" style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid #10B981'
                      }}>
                        <CalendarClock size={18} style={{ color: '#10B981' }} />
                        <span className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                          {scheduled
                            ? `Reserved for ${scheduled.toLocaleDateString()} — ${endDate.toLocaleDateString()}`
                            : "Notified when a slot opens"}
                        </span>
                      </div>

                      <p className="text-xs mb-4" style={{ color: '#8197A4' }}>
                        Booked on {new Date(r.created_at).toLocaleDateString()}
                      </p>

                      <button
                        onClick={() => handleCancelReservation(r.reservation_id)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                        style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444', border: '1px solid #DC2626' }}
                        data-testid={`cancel-reservation-${r.reservation_id}`}
                      >
                        <X size={14} /> Cancel Pre-booking
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Waitlist Section */}
        {waitlist.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#FFFFFF' }}>
              <ListOrdered size={24} style={{ color: '#F0C14B' }} />
              On Waitlist
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {waitlist.map((item) => {
                const waitDays = calculateWaitDays(item.joined_at);
                
                return (
                  <div
                    key={item.waitlist_id}
                    className="p-6 rounded-xl flex items-center gap-6 flex-wrap"
                    style={{ backgroundColor: '#1A2632', border: '1px solid #F0C14B' }}
                    data-testid={`waitlist-item-${item.waitlist_id}`}
                  >
                    <img
                      src={mediaUrl(item.movie?.poster_path, API) || `https://picsum.photos/seed/${item.movie_id}/200/300`}
                      alt={item.movie?.title}
                      className="w-24 h-36 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-[200px]">
                      <h3 className="font-semibold text-xl mb-2" style={{ color: '#FFFFFF' }}>
                        {item.movie?.title || 'Unknown Movie'}
                      </h3>
                      
                      <div className="flex items-center gap-2 mb-3 p-3 rounded-lg inline-flex" style={{
                        backgroundColor: 'rgba(240, 193, 75, 0.15)',
                        border: '1px solid #F0C14B'
                      }}>
                        <AlertCircle size={18} style={{ color: '#F0C14B' }} />
                        <span className="text-sm font-semibold" style={{ color: '#FFFFFF' }} data-testid={`wait-days-${item.waitlist_id}`}>
                          Position #{item.position} • Waiting for {waitDays} day{waitDays !== 1 ? 's' : ''}
                        </span>
                      </div>

                      <p className="text-xs mb-4" style={{ color: '#8197A4' }}>
                        Joined waitlist on {new Date(item.joined_at).toLocaleDateString()} • You&apos;ll be notified when a slot opens
                      </p>

                      <button
                        onClick={() => handleLeaveWaitlist(item.waitlist_id)}
                        className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                        style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
                        data-testid={`leave-waitlist-${item.waitlist_id}`}
                      >
                        Leave Waitlist
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Past Borrows */}
        {pastBorrows.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Borrow History</h2>
            <div className="space-y-3">
              {pastBorrows.map((borrow) => (
                <div
                  key={borrow.borrow_id}
                  className="p-4 rounded-lg flex items-center justify-between"
                  style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}
                >
                  <div>
                    <h3 className="font-medium" style={{ color: '#FFFFFF' }}>
                      {borrow.movie?.title || 'Unknown Movie'}
                    </h3>
                    <p className="text-sm" style={{ color: '#8197A4' }}>
                      Borrowed on {new Date(borrow.borrowed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {borrow.status === 'returned' ? (
                      <CheckCircle size={20} style={{ color: '#10B981' }} />
                    ) : (
                      <XCircle size={20} style={{ color: '#EF4444' }} />
                    )}
                    <span className="text-sm capitalize" style={{ color: '#8197A4' }}>{borrow.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {borrows.length === 0 && waitlist.length === 0 && reservations.length === 0 && (
          <div className="text-center py-16 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
            <p className="text-lg mb-2" style={{ color: '#FFFFFF' }}>No borrowed movies yet</p>
            <p className="text-sm mb-6" style={{ color: '#8197A4' }}>Start exploring the library and borrow your first movie</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: '#3B82F6', color: 'white' }}
              data-testid="browse-movies-button"
            >
              Browse Movies
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
